"use client";

import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";

type CreatorMarqueeProps = {
  data?: { items?: string[] };
};

const DEFAULT_ITEMS = [
  "TikTok",
  "Instagram",
  "Xiaohongshu",
  "Lemon8",
  "YouTube",
  "Facebook",
  "X",
  "Threads",
];

export default function CreatorMarquee({ data }: CreatorMarqueeProps) {
  const items = data?.items?.length ? data.items : DEFAULT_ITEMS;

  const singleCopyRef = useRef<HTMLDivElement>(null);
  const secondHalfRef = useRef<HTMLDivElement>(null);
  const [repeats, setRepeats] = useState(1);
  const [shiftPx, setShiftPx] = useState(0);

  useEffect(() => {
    const recalc = () => {
      const singleWidth = singleCopyRef.current?.offsetWidth || 0;
      if (!singleWidth) return;
      const needed = Math.ceil(window.innerWidth / singleWidth);
      setRepeats(Math.max(1, needed));
    };

    recalc();
    window.addEventListener("resize", recalc);
    return () => window.removeEventListener("resize", recalc);
  }, [items]);

  useLayoutEffect(() => {
    setShiftPx(secondHalfRef.current?.offsetLeft || 0);
  }, [repeats, items]);

  const marqueeDurationSeconds = 24 * repeats;

  return (
    <section className="pt-[10px] pb-10 md:pb-16 overflow-hidden">
      <div className="relative w-screen left-1/2 -translate-x-1/2 overflow-hidden">
        <div
          className="relative flex items-center gap-x-6 md:gap-x-8 w-max animate-brandMarquee motion-reduce:animate-none"
          style={
            {
              animationDuration: `${marqueeDurationSeconds}s`,
              "--brand-shift": `${shiftPx}px`,
            } as CSSProperties
          }
        >
          {[...Array(repeats * 2)].map((_, i) => (
            <div
              key={i}
              ref={
                i === 0
                  ? singleCopyRef
                  : i === repeats
                    ? secondHalfRef
                    : undefined
              }
              className="flex items-center gap-x-6 md:gap-x-8 shrink-0"
            >
              {items.map((item, index) => (
                <React.Fragment key={`${i}-${index}`}>
                  <span className="font-bold uppercase tracking-tight text-md leading-none shrink-0 text-purple">
                    {item}
                  </span>
                  <span className="text-darkGrey/30 text-md leading-none shrink-0">
                    •
                  </span>
                </React.Fragment>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
